import { CreditCard, ArrowUpCircle, ExternalLink, AlertTriangle, ChevronsUpDown, Wifi, Crown, VenetianMask, Zap, Grid, Sheet, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Loader } from "@/components/ui/loader";
import {
  type Subscription,
  fetchSubscription
} from "@/services/api";
import { PricingPlans } from "@/components/PricingPlans";
import { useState, useEffect, useRef } from "react";
import { formatCurrency } from "@/lib/utils";
import { cn } from "@/lib/utils";
import { trackPlanUpgradeCompleted, trackSubscriptionCancelled } from '@/utils/eventTracking';
import { cancelSubscription } from "@/services/paddle";

// Add CSS keyframes for the ripple animation
const rippleStyle = `
@keyframes ripple {
  0% {
    transform: scale(0.8);
    opacity: 0;
  }
  50% {
    transform: scale(1.5);
    opacity: 0.3;
  }
  100% {
    transform: scale(2.2);
    opacity: 0;
  }
}

.card-container {
  position: relative;
  overflow: hidden;
}

.ripple {
  position: absolute;
  top: 50%;
  right: 15px;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.1);
}

.ripple:before,
.ripple:after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.1);
  animation: ripple 2s infinite cubic-bezier(0.65, 0, 0.35, 1);
}

.ripple:after {
  animation-delay: 0.5s;
}
`;

interface SubscriptionContentProps {
  premiumUntil: string | null;
  reload: boolean;
  userInfo: {
    id: string;
    email: string;
    name: string;
  };
}

type PaymentMethodType =
  | 'alipay'
  | 'apple_pay'
  | 'bancontact'
  | 'card'
  | 'google_pay'
  | 'ideal'
  | 'offline'
  | 'paypal'
  | 'unknown'
  | 'wire_transfer';

// Payment method icons mapping
const PaymentMethodIcon = ({ type }: { type: PaymentMethodType }) => {
  switch (type) {
    case 'card':
      return <CreditCard className="h-8 w-8 text-blue-600" />;
    case 'paypal':
      return (
        <div className="relative flex h-8 w-8 items-center justify-center rounded-full bg-[#0070ba] text-white">
          <span className="font-bold text-xs">P</span>
        </div>
      );
    case 'apple_pay':
      return (
        <div className="relative flex h-8 w-8 items-center justify-center rounded-full bg-black text-white">
          <span className="font-bold text-xs">A</span>
        </div>
      );
    case 'google_pay':
      return (
        <div className="relative flex h-8 w-8 items-center justify-center rounded-full bg-white border border-gray-200">
          <span className="font-bold text-blue-600 text-xs">G</span>
        </div>
      );
    case 'alipay':
      return (
        <div className="relative flex h-8 w-8 items-center justify-center rounded-full bg-[#00a0e9] text-white">
          <span className="font-bold text-xs">Ali</span>
        </div>
      );
    case 'wire_transfer':
      return <ArrowUpCircle className="h-8 w-8 text-green-600" />;
    case 'bancontact':
    case 'ideal':
      return <ExternalLink className="h-8 w-8 text-purple-600" />;
    case 'offline':
      return <ChevronsUpDown className="h-8 w-8 text-gray-600" />;
    case 'unknown':
    default:
      return <AlertTriangle className="h-8 w-8 text-yellow-600" />;
  }
};

// Get card type icon and style
const getCardDetails = (cardType?: string) => {
  switch (cardType?.toLowerCase()) {
    case 'visa':
      return {
        className: 'bg-gradient-to-br from-teal-400/30 to-teal-900/50',
        textColor: 'text-white',
        brand: 'VISA'
      };
    case 'mastercard':
      return {
        className: 'bg-gradient-to-br from-slate-500/40 to-slate-900/60',
        textColor: 'text-white',
        brand: 'MASTERCARD'
      };
    case 'amex':
    case 'american express':
      return {
        className: 'bg-gradient-to-br from-blue-400/30 to-blue-900/50',
        textColor: 'text-white',
        brand: 'AMEX'
      };
    case 'discover':
      return {
        className: 'bg-gradient-to-br from-orange-400/30 to-orange-900/50',
        textColor: 'text-white',
        brand: 'DISCOVER'
      };
    default:
      return {
        className: 'bg-gradient-to-br from-slate-400/30 to-slate-900/50',
        textColor: 'text-white',
        brand: 'CARD'
      };
  }
};

// Helper function to get current plan details - exported for reuse
export const getCurrentPlan = (subscription: Subscription | null, premiumUntil: string | null) => {
  // Check if user has any premium access (including FREE plans with validity)
  const expiryDate = premiumUntil ? new Date(premiumUntil) : null;
  const isExpired = expiryDate && expiryDate < new Date();

  if (!subscription || !['active', 'trialing', 'paused', 'past_due'].includes(subscription.status)) {
    // Handle FREE plan with potential validity
    const subscriptionStatus = expiryDate ? (isExpired ? 'EXPIRED' : 'ACTIVE') : null;
    const validity = expiryDate ? expiryDate.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    }) : null;

    return {
      name: 'Starter',
      description: 'Basic features included',
      icon: <Zap className="h-6 w-6 text-gray-400" />,
      bgColor: 'bg-gray-800',
      borderColor: 'border-gray-700',
      textColor: 'text-white',
      badgeColor: 'bg-gray-700 text-gray-300',
      amount: null,
      subscriptionStatus: subscriptionStatus,
      validity: validity
    };
  }

  // Determine plan based on subscription pricing config plan type
  const planType = subscription.pricing_config.plan_type;
  const isActive = new Date(subscription.premium_until) >= new Date();
  const validityDate = new Date(subscription.premium_until);

  const baseConfig = {
    amount: subscription.latest_payment ? formatCurrency(subscription.latest_payment.amount, subscription.latest_payment?.currency || 'USD') : null,
    subscriptionStatus: isActive ? 'ACTIVE' : 'EXPIRED',
    validity: validityDate.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    })
  };

  // Check plan type from pricing config
  if (planType === 'EXPORT') {
    return {
      name: 'Export Pass',
      description: 'Structured exports to Google Sheets and Notion.',
      icon: <Sheet className="h-6 w-6 text-purple-400" />,
      bgColor: 'bg-gray-800',
      borderColor: 'border-purple-500',
      textColor: 'text-white',
      badgeColor: 'bg-purple-600 text-white',
      ...baseConfig
    };
  } else if (planType === 'PRO') {
    return {
      name: 'PRO',
      description: 'Access to premium dashboard features',
      icon: <VenetianMask className="h-6 w-6 text-blue-400" />,
      bgColor: 'bg-gray-800',
      borderColor: 'border-blue-500',
      textColor: 'text-white',
      badgeColor: 'bg-blue-600 text-white',
      ...baseConfig
    };
  } else if (planType === 'PRO_TRIAL') {
    return {
      name: 'PRO',
      description: 'Access to premium dashboard features',
      icon: <VenetianMask className="h-6 w-6 text-blue-400" />,
      bgColor: 'bg-gray-800',
      borderColor: 'border-blue-500',
      textColor: 'text-white',
      badgeColor: 'bg-blue-600 text-white',
      ...baseConfig
    };
  } else {
    // Fallback for any other plan types or if plan_type is not recognized
    return {
      name: planType || 'PREMIUM',
      description: 'Premium subscription active',
      icon: <Crown className="h-6 w-6 text-green-400" />,
      bgColor: 'bg-gray-800',
      borderColor: 'border-green-500',
      textColor: 'text-white',
      badgeColor: 'bg-green-600 text-white',
      ...baseConfig
    };
  }
};

export function SubscriptionContent({ premiumUntil, reload, userInfo }: SubscriptionContentProps) {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Add style element for ripple animation
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.innerHTML = rippleStyle;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  useEffect(() => {

    const loadSubscription = async () => {
      try {
        const subscriptionData = await fetchSubscription();
        const previousSubscription = subscription;
        setSubscription(subscriptionData);
        const isActive = subscriptionData?.status === 'active';

        // Track plan upgrade completion if subscription becomes active and we were polling (reload=true)
        if (isActive && reload && (!previousSubscription || previousSubscription.status !== 'active')) {
          const planName = subscriptionData?.pricing_config?.plan_type || 'unknown';
          const paymentMethod = subscriptionData?.latest_payment?.payment_method_detail?.type || 'unknown';
          const amount = subscriptionData?.latest_payment?.amount;
          
          trackPlanUpgradeCompleted(planName, paymentMethod, amount);
        }

        // Track subscription cancellation if status changes to cancelled
        if (subscriptionData?.status === 'canceled' && (!previousSubscription || previousSubscription.status !== 'canceled')) {
          const planName = subscriptionData?.pricing_config?.plan_type || 'unknown';
          trackSubscriptionCancelled(planName);
        }

        if (isActive) {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Failed to load subscription:', error);
      } finally {
        setIsLoading(false)
        setIsLoadingSubscription(false);
      }
    };

    // Initial load
    loadSubscription();

    // Start polling only if reload is true and no active subscription
    if (reload) {
      pollingIntervalRef.current = setInterval(loadSubscription, 3000);
    }

    // Cleanup function
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [reload]); // Only re-run when reload changes


  const renderFrequencySelector = () => {
    // This function is now replaced by the pricing plans component
    const currentPlan = subscription?.pricing_config?.plan_type || 'free';
    return <PricingPlans userInfo={userInfo} currentPlan={currentPlan} />;
  };

  const renderPaymentMethodCard = () => {
    if (!subscription?.latest_payment?.payment_method_detail) {
      return (
        <div className="border border-gray-700 rounded-lg p-4 bg-gray-800">
          <h3 className="font-semibold mb-2 text-white">Payment Method</h3>
          <div className="flex items-center gap-2 text-gray-400">
            <CreditCard className="h-5 w-5" />
            <span>•••• •••• •••• ••••</span>
          </div>
        </div>
      );
    }

    const methodDetail = subscription.latest_payment.payment_method_detail;
    const paymentType = (methodDetail.type as PaymentMethodType) || 'unknown';

    if (paymentType === 'card') {
      // Get card details based on the type
      const cardDetails = getCardDetails(methodDetail.card_type);

      // Format expiry date
      const formattedExpiry = methodDetail.expiry_month && methodDetail.expiry_year
        ? `${methodDetail.expiry_month.toString().padStart(2, '0')}/${methodDetail.expiry_year.toString().slice(-2)}`
        : null;

      // Format name to show properly
      const cardholderName = (userInfo.name || 'Card Holder').toUpperCase();

      // Format card number with proper spacing
      const cardNumber = methodDetail.last_four_digits
        ? `•••• •••• •••• ${methodDetail.last_four_digits}`
        : '•••• •••• ••••';

      return (
        <div>
          <h3 className="font-semibold mb-2 text-white">Payment Method</h3>
          <div className={cn(
            "card-container rounded-xl h-52 w-full relative overflow-hidden shadow-lg backdrop-blur-sm",
            cardDetails.className
          )}>
            {/* Card Issuer Name */}
            <div className="font-medium text-white/90 mt-6 ml-6">
              <p className="uppercase font-medium text-bold tracking-wide">{methodDetail.card_type}</p>
            </div>

            {/* Contactless Icon */}
            <div className="absolute top-5 right-5 rotate-90">
              <Wifi className="h-6 w-6 text-white/80" />
            </div>

            {/* Ripple Animation */}
            <div className="ripple"></div>

            {/* Cardholder Name */}
            <div className="absolute bottom-12 left-5">
              <div className="text-white font-medium uppercase">
                {cardholderName}
              </div>
            </div>

            {/* Expiry */}
            <div className="absolute bottom-5 right-5">
              <div className="text-white/90 text-lg font-medium">
                {formattedExpiry || 'MM/YY'}
              </div>
            </div>

            {/* Card Number */}
            <div className="absolute bottom-5 left-5 right-5">
              <div className="text-white/90 font-mono text-sm tracking-wider">
                {cardNumber}
              </div>
            </div>

            {/* Card Brand Symbol */}
            <div className="absolute bottom-12 right-5">
              <div className="flex space-x-1">
                <div className="w-5 h-5 rounded-full bg-white/90 opacity-60"></div>
                <div className="w-5 h-5 rounded-full bg-white/70 opacity-80"></div>
              </div>
            </div>

            {/* Frosted Glass Background Effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-black/20"></div>
          </div>
        </div>
      );
    } else {
      // Non-card payment methods
      return (
        <div>
          <h3 className="font-semibold mb-2 text-white">Payment Method</h3>
          <div className="border border-gray-700 rounded-lg p-4 flex items-start gap-3 bg-gray-800">
            <PaymentMethodIcon type={paymentType} />
            <div>
              <div className="font-medium capitalize text-white">{paymentType.replace('_', ' ')}</div>
              <div className="text-sm text-gray-400 mt-2">
                Last payment: {subscription.latest_payment ? formatCurrency(subscription.latest_payment.amount, subscription.latest_payment.currency) : 'N/A'}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Transaction ID: {subscription.latest_payment?.paddle_payment_id || 'N/A'}
              </div>
            </div>
          </div>
        </div>
      );
    }
  };

  const renderLatestPayment = () => {
    if (!subscription?.latest_payment) return null;

    return (
      <div>
        <h3 className="font-semibold mb-2 text-white">Latest Payment</h3>
        <div className="border border-gray-700 rounded-lg h-52 p-4 bg-gray-800">
          <div className="flex flex-col justify-between h-full">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <p className="text-gray-400 font-medium">Amount:</p>
                <span className="font-semibold text-white">
                  {subscription.latest_payment ? formatCurrency(subscription.latest_payment.amount, subscription.latest_payment.currency) : 'N/A'}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <p className="text-gray-400 font-medium">Date:</p>
                <span className="font-semibold text-white">
                  {subscription.latest_payment ? new Date(subscription.latest_payment.created_at).toLocaleDateString('en-US', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric'
                  }) : 'N/A'}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <p className="text-gray-400 font-medium">Status:</p>
                <span className="font-semibold capitalize text-white">
                  {subscription.latest_payment?.status || 'N/A'}
                </span>
              </div>
            </div>

            <div className="mt-auto pt-4 border-t border-gray-700 text-xs text-gray-500">
              Transaction ID: {subscription.latest_payment?.paddle_payment_id || 'N/A'}
            </div>
          </div>
        </div>
      </div>
    );
  };



  const renderCurrentPlan = () => {
    if (isLoadingSubscription) {
      return (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-white">Current Plan</h3>
          </div>
          <div className="p-6 rounded-xl border-2 border-gray-700 bg-gray-800 shadow-sm">
            <Loader size="md" showText={true} text="Loading plan information..." className="text-gray-400" />
          </div>
        </div>
      );
    }

    const plan = getCurrentPlan(subscription, premiumUntil);

    return (
      <div className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-white">Current Plan</h3>
        </div>

        <div className={cn(
          "p-6 rounded-xl border-2 shadow-sm",
          plan.bgColor,
          plan.borderColor
        )}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-gray-700 shadow-sm">
                {plan.icon}
              </div>
              <div>
                <h4 className={cn("text-2xl font-bold", plan.textColor)}>
                  {plan.name}
                </h4>
                <p className="text-gray-100 mt-1">{plan.description}</p>
                {plan.name === 'FREE' && (
                  <p className="text-sm text-gray-300 mt-2">
                    Upgrade to unlock premium features and unlimited access
                  </p>
                )}
              </div>
            </div>

            {plan.subscriptionStatus ? (
              <div className="text-right">
                <div className={cn(
                  "px-3 py-1 rounded-full text-sm font-medium mb-2 text-center",
                  plan.subscriptionStatus === 'ACTIVE'
                    ? "bg-green-600 text-white"
                    : "bg-red-600 text-white"
                )}>
                  {plan.subscriptionStatus}
                </div>
                <div className="text-sm text-gray-300">
                  {plan.subscriptionStatus === 'ACTIVE' ? 'Valid until' : 'Expired on'}
                </div>
                <div className="text-sm font-medium text-white">
                  {plan.validity}
                </div>
              </div>
            ) : (
              <div className="text-right">
                <div className="px-3 py-1 rounded-full text-sm font-medium bg-gray-700 text-gray-300 mb-2">
                  FREE
                </div>
                <div className="text-sm text-gray-400">No expiry</div>
              </div>
            )}
          </div>

          {plan.name === 'FREE' && (
            <div className="mt-4 pt-4 border-t border-gray-700">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-300">Ready to upgrade?</span>
                <Button
                  size="sm"
                  className="bg-purple-600 hover:bg-purple-700"
                  onClick={() => {
                    // Scroll to pricing plans
                    const pricingSection = document.querySelector('.space-y-4');
                    pricingSection?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  View Plans
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderSubscriptionContent = () => {
    if (isLoading || isLoadingSubscription) {
      return (
        <div className="p-4 bg-gray-800 rounded-lg border border-gray-700">
          <Loader size="md" showText={true} text="Loading subscription information..." className="text-gray-400" />
        </div>
      );
    }


    // If we have subscription data and it's active, show additional management options

    if (subscription && ['active', 'trialing', 'paused', 'past_due'].includes(subscription.status) && new Date(subscription.premium_until) >= new Date()) {
      return (
        <>
          {/* Payment method and latest payment side by side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>{renderPaymentMethodCard()}</div>
            <div>{renderLatestPayment()}</div>
          </div>

          {/* Subscription management buttons */}
          <div className="flex flex-col sm:flex-row gap-4 mt-6">
            {subscription.update_payment_method_link && (
              <Button
                variant="outline"
                className="flex-1 bg-gray-800 border-gray-600 text-white hover:bg-gray-700"
                onClick={() => window.open(subscription.update_payment_method_link, '_blank')}
              >
                Manage Payment Method
              </Button>
            )}
            {subscription.cancel_subscription_link && subscription.pricing_config.plan_type !== 'EXPORT' && (
              <Button
                variant="destructive"
                className="flex-1"
                onClick={() => {
                  if (subscription.status === 'trialing') {
                    window.open(subscription.cancel_subscription_link, '_blank', 'noopener,noreferrer');
                  } else {
                    cancelSubscription(subscription.paddle_subscription_id, () => {
                      setTimeout(() => {
                        window.location.href = `${window.location.origin}/profile?active=subscription&reload=true`;
                      }, 5000);
                    });
                  }
                }}
              >
                Cancel Subscription
              </Button>
            )}
          </div>
        </>
      );
    }

    // For non-active subscriptions, show pricing plans
    return renderFrequencySelector();
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-white">Subscription Management</h2>

      {/* Current Plan Section */}
      {renderCurrentPlan()}

      <div className="space-y-4">
        {renderSubscriptionContent()}
      </div>
    </div>
  );
} 